import { MODULES } from "@/config";
import type { Attribute } from "@/attributes";
import type { RawAttribute, UserDetail } from "@/api";

export interface ModuleValue {
    normal: number;
    admin: number;
    super: number;
}

export type ModuleField = "normal" | "admin" | "super";

type ModuleInternalValue = 0 | 1 | 2;
type ModuleCharacter = "A" | "C" | "D";

export type ModuleAttribute = Attribute<ModuleValue, string>;

export const parseUsersModules = (
    user: UserDetail
): Record<string, ModuleAttribute> => {
    const modules: Record<string, ModuleAttribute> = {};

    for (const key of MODULES) {
        if (key in user.attributes) {
            if (user.attributes[key].whocanset === null) {
                continue;
            }

            modules[key] = parseModuleAttribute(key, user.attributes[key]);
        }
    }

    return modules;
};

// The character each level is stored as, indexed by the level's value.
const MODULE_CHARACTERS = "ACD";

export const MODULE_VALUE_LABELS = ["Off", "Restricted", "On"];

const MODULE_LEVEL_LABELS = ["Normal", "Admin", "Super"];

const parseModuleCharacter = (moduleCharacter: string): ModuleInternalValue => {
    const value = MODULE_CHARACTERS.indexOf(moduleCharacter);

    if (value < 0) {
        throw `Wrong module chracter ${moduleCharacter}`;
    }

    return value as ModuleInternalValue;
};

const getModuleCharacter = (value: number): ModuleCharacter => {
    const moduleCharacter = MODULE_CHARACTERS[value];

    if (moduleCharacter === undefined) {
        throw `Wrong module value ${value}`;
    }

    return moduleCharacter as ModuleCharacter;
};

// The stored value is not always three of the characters we know, so this
// reads what it can and leaves the caller to fall back to the raw string.
const readModuleValue = (value: string | null): string[] | null => {
    const labels = [...(value ?? "").slice(0, MODULE_LEVEL_LABELS.length)].map(
        (character) => MODULE_VALUE_LABELS[MODULE_CHARACTERS.indexOf(character)]
    );

    return labels.length === MODULE_LEVEL_LABELS.length &&
        labels.every(Boolean)
        ? labels
        : null;
};

export const describeModuleValue = (value: string): string =>
    readModuleValue(value)?.join(", ") ?? value;

// Only the levels that moved.
export const describeModuleMove = (
    from: string | null,
    to: string | null
): string | null => {
    const before = readModuleValue(from);
    const after = readModuleValue(to);

    if (from === null && after) {
        return `Not set → ${after.join(", ")}`;
    }

    if (to === null && before) {
        return `${before.join(", ")} → unset`;
    }

    if (!before || !after) {
        return null;
    }

    const moved = MODULE_LEVEL_LABELS.map((level, index) =>
        before[index] === after[index]
            ? null
            : `${level}: ${before[index]} → ${after[index]}`
    ).filter((line) => line !== null);

    return moved.length ? moved.join("\n") : null;
};

const parseModuleValue = (value: string): ModuleValue => {
    return {
        normal: parseModuleCharacter(value[0]),
        admin: parseModuleCharacter(value[1]),
        super: parseModuleCharacter(value[2]),
    };
};

const parseModuleAttribute = (
    key: string,
    attribute: RawAttribute
): ModuleAttribute => {
    return {
        key: key,
        value: parseModuleValue(attribute.value),
        inherit: attribute.inherit,
        inheritValue: parseModuleValue(attribute.inherit_value),
        inheritLineage: attribute.inherit_lineage,
        whocanset: attribute.whocanset,
        whocansetLineage: attribute.whocanset_lineage,
        default: attribute.default,
        getActiveValue() {
            return this.inherit ? this.inheritValue : this.value;
        },
        getValueRepresentation() {
            let n = getModuleCharacter(this.value.normal);
            let a = getModuleCharacter(this.value.admin);
            let s = getModuleCharacter(this.value.super);

            return `${n}${a}${s}D`;
        },
        canBeSet() {
            return this.whocanset !== null;
        },
    };
};

export const areModulesEqual = (
    first: ModuleAttribute,
    second: ModuleAttribute
): boolean => {
    if (first.inherit !== second.inherit) {
        return false;
    }

    if (first.whocanset !== second.whocanset) {
        return false;
    }

    if (
        first.value.normal !== second.value.normal ||
        first.value.admin !== second.value.admin ||
        first.value.super !== second.value.super
    ) {
        return false;
    }

    return true;
};

export const incrementModule = (
    value: ModuleValue,
    moduleField: ModuleField
): ModuleValue => {
    switch (moduleField) {
        case "normal":
            value.normal = Math.min(value.normal + 1, 2);
            value.admin = Math.max(value.normal, value.admin);
            value.super = Math.max(value.normal, value.super);
            break;
        case "admin":
            value.admin = Math.min(value.admin + 1, 2);
            value.super = Math.max(value.admin, value.super);
            break;
        case "super":
            value.super = Math.min(value.super + 1, 2);
            break;
        default:
            throw `Unknown module type ${moduleField}`;
    }

    return value;
};

export const decrementModule = (
    value: ModuleValue,
    moduleField: ModuleField
): ModuleValue => {
    switch (moduleField) {
        case "normal":
            value.normal = Math.max(value.normal - 1, 0);
            break;
        case "admin":
            value.admin = Math.max(value.admin - 1, 0);
            value.normal = Math.min(value.normal, value.admin);
            break;
        case "super":
            value.super = Math.max(value.super - 1, 0);
            value.normal = Math.min(value.normal, value.super);
            value.admin = Math.min(value.admin, value.super);
            break;
        default:
            throw `Unknown module type ${moduleField}`;
    }

    return value;
};

export const setModuleValue = (module: ModuleValue, value: string) => {
    module.normal = parseModuleCharacter(value[0]);
    module.admin = parseModuleCharacter(value[1]);
    module.super = parseModuleCharacter(value[2]);

    return module;
};
